import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Calendar, Clock, Wallet, User as UserIcon, Send, CheckCircle2, XCircle, Hourglass, Plus, Briefcase, LogIn, LogOut, Save, IdCard, Phone, Mail, MapPin, Landmark } from "lucide-react";

const STATUS = {
  pending: { label: "Në pritje", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/40", Icon: Hourglass },
  approved: { label: "Aprovuar", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/40", Icon: CheckCircle2 },
  rejected: { label: "Refuzuar", color: "text-red-400", bg: "bg-red-500/10 border-red-500/40", Icon: XCircle },
};
const PAY_STATUS = {
  paid: { label: "E paguar", color: "text-emerald-400" },
  partial: { label: "Pjesërisht", color: "text-orange-400" },
  unpaid: { label: "E papaguar", color: "text-red-400" },
};
const MONTH_NAMES = ["", "Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"];

const fmtDur = (h) => {
  if (!h && h !== 0) return "0h 0m";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${mm}m`;
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const CONTRACT_LABELS = {
  permanent: "E përhershme",
  probation: "Provë",
  fixed_1y: "1 vjeçar",
  other: "Tjetër",
};

export default function PosProfile() {
  const { user } = useApp();
  const [tab, setTab] = useState("dash");
  const [dash, setDash] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  // Timesheet
  const now = new Date();
  const [tsY, setTsY] = useState(now.getFullYear());
  const [tsM, setTsM] = useState(now.getMonth() + 1);
  const [timesheet, setTimesheet] = useState(null);
  const [shift, setShift] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  // Profile
  const [profile, setProfile] = useState(null);
  const [profDraft, setProfDraft] = useState(null);
  const [profSaving, setProfSaving] = useState(false);

  const loadAll = async () => {
    const [d, v, s, sh, p] = await Promise.all([
      api.get("/pos/me/dashboard"),
      api.get("/pos/me/vacations"),
      api.get("/pos/me/salaries"),
      api.get("/pos/me/current-shift"),
      api.get("/pos/me/profile"),
    ]);
    setDash(d.data); setVacations(v.data); setSalaries(s.data);
    setShift(sh.data?.shift || null);
    setProfile(p.data); setProfDraft(p.data);
  };
  const loadTimesheet = async (y = tsY, m = tsM) => {
    const r = await api.get(`/pos/me/timesheet?year=${y}&month=${m}`);
    setTimesheet(r.data);
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === "time") loadTimesheet(tsY, tsM); }, [tab, tsY, tsM]);

  // Live shift duration
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!shift) return;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [shift]);
  const liveShiftHours = shift ? Math.max(0, (nowTick - new Date(shift.clock_in).getTime()) / 3600000) : 0;

  const submitVac = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Zgjidh datat"); return; }
    try {
      await api.post("/pos/me/vacations", form);
      toast.success("Kërkesa u dërgua");
      setShowForm(false); setForm({ start_date: "", end_date: "", reason: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Gabim"); }
  };
  const daysBetween = () => {
    if (!form.start_date || !form.end_date) return 0;
    const d1 = new Date(form.start_date), d2 = new Date(form.end_date);
    if (d2 < d1) return 0;
    return Math.round((d2 - d1) / 86400000) + 1;
  };

  const clockIn = async () => {
    setClockLoading(true);
    try {
      await api.post("/pos/me/clock-in");
      toast.success("Ke hyrë në punë");
      const sh = await api.get("/pos/me/current-shift");
      setShift(sh.data?.shift || null);
      if (tab === "time") loadTimesheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
      try { const sh = await api.get("/pos/me/current-shift"); setShift(sh.data?.shift || null); } catch {}
    }
    setClockLoading(false);
  };
  const clockOut = async () => {
    if (!window.confirm("Dilni nga puna?")) return;
    setClockLoading(true);
    try {
      const r = await api.post("/pos/me/clock-out");
      toast.success(`Dolët. Orë të punuara: ${fmtDur(r.data.duration_hours)}`);
      setShift(null);
      if (tab === "time") loadTimesheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
      try { const sh = await api.get("/pos/me/current-shift"); setShift(sh.data?.shift || null); } catch {}
    }
    setClockLoading(false);
  };

  const saveProfile = async () => {
    setProfSaving(true);
    try {
      const payload = {};
      ["first_name","last_name","phone","email","address","iban","bank_name","personal_id"].forEach(k => {
        if ((profDraft?.[k] ?? "") !== (profile?.[k] ?? "")) payload[k] = profDraft[k] || "";
      });
      if (Object.keys(payload).length === 0) { toast.info("Asnjë ndryshim"); setProfSaving(false); return; }
      await api.put("/pos/me/profile", payload);
      toast.success("Të dhënat u ruajtën");
      setProfile(profDraft);
    } catch (e) { toast.error(e.response?.data?.detail || "Gabim"); }
    setProfSaving(false);
  };

  return (
    <div className="p-4 max-w-4xl mx-auto pb-24 lg:pb-6">
      <div className="hg-card p-4 mb-4 flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[#FFD600] text-black flex items-center justify-center">
          <UserIcon size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl truncate" data-testid="pos-profile-name">{dash?.worker_name || user?.name || "Punëtor"}</div>
          <div className="text-xs text-neutral-400">Punëtor HidroGV</div>
          {dash?.hire_date && <div className="text-[10px] text-neutral-500 mt-0.5">Punëson që nga: {dash.hire_date}</div>}
        </div>
        {shift ? (
          <button onClick={clockOut} disabled={clockLoading} data-testid="clock-out-header"
            className="bg-red-500 hover:bg-red-400 text-white text-[10px] uppercase font-bold px-3 py-2 inline-flex items-center gap-1">
            <LogOut size={12} /> Dal ({fmtDur(liveShiftHours)})
          </button>
        ) : (
          <button onClick={clockIn} disabled={clockLoading} data-testid="clock-in-header"
            className="bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] uppercase font-bold px-3 py-2 inline-flex items-center gap-1">
            <LogIn size={12} /> Hyj në punë
          </button>
        )}
      </div>

      <div className="grid grid-cols-5 gap-1 mb-4 p-1 bg-[#131E30] border border-[#1F2B44] overflow-x-auto">
        {[
          { k: "dash", Ic: Briefcase, label: "Dashboard" },
          { k: "time", Ic: Clock, label: "Ora e punës" },
          { k: "vac", Ic: Calendar, label: "Pushimet" },
          { k: "sal", Ic: Wallet, label: "Paga" },
          { k: "prof", Ic: IdCard, label: "Të dhënat" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`py-2.5 px-1 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${tab === t.k ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
            <t.Ic size={12} className="inline mr-1 -mt-0.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "dash" && dash && (
        <div className="grid grid-cols-2 gap-3">
          <div className="hg-card p-4 border-l-4 border-emerald-400" data-testid="dash-today">
            <div className="text-[10px] uppercase text-neutral-400 tracking-wider mb-1">Shitja sot</div>
            <div className="font-display text-2xl text-emerald-400">{fmtEUR(dash.today_revenue)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">{dash.today_count} fatura</div>
          </div>
          <div className="hg-card p-4 border-l-4 border-cyan-400" data-testid="dash-month">
            <div className="text-[10px] uppercase text-neutral-400 tracking-wider mb-1">Shitja këtë muaj</div>
            <div className="font-display text-2xl text-cyan-400">{fmtEUR(dash.month_revenue)}</div>
            <div className="text-[10px] text-neutral-500 mt-1">{dash.month_count} fatura</div>
          </div>
          <div className="hg-card p-4 border-l-4 border-[#FFD600]" data-testid="dash-vac">
            <div className="text-[10px] uppercase text-neutral-400 tracking-wider mb-1">Pushime të mbetura</div>
            <div className="font-display text-2xl text-[#FFD600]">{dash.vacation_remaining} <span className="text-sm">ditë</span></div>
            <div className="text-[10px] text-neutral-500 mt-1">Total: {dash.vacation_total} • Përdorur: {dash.vacation_used}</div>
            {dash.vacation_pending > 0 && <div className="text-[10px] text-orange-400 mt-1">{dash.vacation_pending} kërkesa në pritje</div>}
          </div>
          <div className="hg-card p-4 border-l-4 border-purple-400" data-testid="dash-sal">
            <div className="text-[10px] uppercase text-neutral-400 tracking-wider mb-1">Paga këtë muaj</div>
            {dash.current_month_salary ? (
              <>
                <div className="font-display text-2xl text-purple-400">{fmtEUR(dash.current_month_salary.total)}</div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  E paguar: <span className="text-emerald-400">{fmtEUR(dash.current_month_salary.paid)}</span>
                  {dash.current_month_salary.remaining > 0 && <> • Mbetur: <span className="text-orange-400">{fmtEUR(dash.current_month_salary.remaining)}</span></>}
                </div>
              </>
            ) : (
              <>
                <div className="font-display text-2xl text-neutral-500">—</div>
                <div className="text-[10px] text-neutral-500 mt-1">Ende s'ka regjistrim</div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "time" && (
        <div>
          <div className="hg-card p-4 mb-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase text-neutral-400 tracking-wider">Statusi</div>
                {shift ? (
                  <div className="font-display text-xl text-emerald-400">Në punë • {fmtDur(liveShiftHours)}</div>
                ) : (
                  <div className="font-display text-xl text-neutral-400">Jashtë orës</div>
                )}
                {shift && <div className="text-[10px] text-neutral-500 mt-0.5">Hyrë: {fmtTime(shift.clock_in)}</div>}
              </div>
              {shift ? (
                <button onClick={clockOut} disabled={clockLoading} data-testid="clock-out-btn"
                  className="bg-red-500 hover:bg-red-400 text-white uppercase font-bold px-4 py-2 inline-flex items-center gap-1.5">
                  <LogOut size={16} /> Dal
                </button>
              ) : (
                <button onClick={clockIn} disabled={clockLoading} data-testid="clock-in-btn"
                  className="bg-emerald-500 hover:bg-emerald-400 text-black uppercase font-bold px-4 py-2 inline-flex items-center gap-1.5">
                  <LogIn size={16} /> Hyj
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <select value={tsM} onChange={e => setTsM(parseInt(e.target.value))} data-testid="ts-month" className="bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm">
              {MONTH_NAMES.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
            </select>
            <input type="number" value={tsY} onChange={e => setTsY(parseInt(e.target.value))} data-testid="ts-year" className="w-20 bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
          </div>

          {timesheet && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Orë Normale</div><div className="text-xl font-display text-emerald-400">{timesheet.hours_regular}</div></div>
                <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Orë Shtesë</div><div className="text-xl font-display text-orange-400">{timesheet.hours_overtime}</div></div>
                <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Total</div><div className="text-xl font-display text-[#FFD600]">{timesheet.hours_total}</div></div>
              </div>
              <div className="hg-card overflow-x-auto">
                {timesheet.entries.length === 0 ? (
                  <div className="p-6 text-center text-neutral-500 text-sm">S'ka regjistrime</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase text-neutral-400 border-b border-[#1F2B44]">
                      <tr><th className="text-left px-2 py-2">Data</th><th className="text-left px-2 py-2">Hyrja</th><th className="text-left px-2 py-2">Dalja</th><th className="text-right px-2 py-2">Orë</th></tr>
                    </thead>
                    <tbody>
                      {timesheet.entries.map(e => (
                        <tr key={e.id} className="border-b border-[#1F2B44]/50" data-testid={`ts-entry-${e.id}`}>
                          <td className="px-2 py-2 text-xs">{e.date}</td>
                          <td className="px-2 py-2 text-xs">{fmtTime(e.clock_in)}</td>
                          <td className="px-2 py-2 text-xs">{e.clock_out ? fmtTime(e.clock_out) : <span className="text-emerald-400">Në vazhdim</span>}</td>
                          <td className="px-2 py-2 text-xs text-right font-bold">{fmtDur(e.duration_hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "vac" && dash && (
        <div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Totali</div><div className="text-xl font-display">{dash.vacation_total}</div></div>
            <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Përdorur</div><div className="text-xl font-display text-orange-400">{dash.vacation_used}</div></div>
            <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Mbetur</div><div className="text-xl font-display text-emerald-400">{dash.vacation_remaining}</div></div>
            <div className="hg-card p-3 text-center"><div className="text-[9px] uppercase text-neutral-400">Në pritje</div><div className="text-xl font-display text-yellow-400">{dash.vacation_pending}</div></div>
          </div>
          <button onClick={() => setShowForm(!showForm)} data-testid="vac-new"
            className="w-full mb-3 hg-btn-primary py-2.5 text-xs uppercase tracking-wider flex items-center justify-center gap-1"><Plus size={14} /> Dërgo kërkesë të re</button>
          {showForm && (
            <div className="hg-card p-4 mb-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-neutral-400">Nga data</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                    data-testid="vac-start"
                    className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-neutral-400">Deri</label>
                  <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                    data-testid="vac-end"
                    className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm mt-0.5" />
                </div>
              </div>
              <div className="text-xs text-neutral-400">Ditë: <span className="text-[#FFD600] font-bold">{daysBetween()}</span></div>
              <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
                placeholder="Arsyeja (opsional)" data-testid="vac-reason"
                rows={2} className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-1.5 text-sm" />
              <div className="flex gap-2">
                <button onClick={submitVac} data-testid="vac-submit" className="flex-1 hg-btn-primary py-2 text-xs uppercase inline-flex items-center justify-center gap-1"><Send size={12} /> Dërgo</button>
                <button onClick={() => setShowForm(false)} className="px-4 border border-[#1F2B44] text-xs uppercase">Anulo</button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {vacations.length === 0 ? (
              <div className="hg-card p-6 text-center text-neutral-400 text-sm">Ende s'ka kërkesa</div>
            ) : vacations.map(v => {
              const st = STATUS[v.status] || STATUS.pending;
              const StI = st.Icon;
              return (
                <div key={v.id} className={`hg-card p-3 border-l-4 ${st.bg}`} data-testid={`vac-${v.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold">{v.start_date} → {v.end_date}</div>
                      <div className="text-xs text-neutral-400">{v.days} ditë</div>
                      {v.reason && <div className="text-xs text-neutral-300 mt-1">"{v.reason}"</div>}
                      {v.admin_note && v.status === "rejected" && <div className="text-xs text-red-300 mt-1">Arsyeja: {v.admin_note}</div>}
                    </div>
                    <div className={`text-xs uppercase font-bold px-2 py-1 border ${st.bg} ${st.color} flex items-center gap-1 flex-shrink-0`}>
                      <StI size={12} /> {st.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "sal" && (
        <div className="space-y-3">
          {salaries.length === 0 ? (
            <div className="hg-card p-6 text-center text-neutral-400 text-sm">Ende s'ka regjistrim page</div>
          ) : salaries.map(s => {
            const ps = PAY_STATUS[s.status] || PAY_STATUS.unpaid;
            return (
              <div key={s.id} className="hg-card p-4" data-testid={`sal-${s.year}-${s.month}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase text-neutral-400">Paga</div>
                    <div className="font-display text-xl">{MONTH_NAMES[s.month]} {s.year}</div>
                  </div>
                  <div className={`text-xs uppercase font-bold ${ps.color}`}>{ps.label}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-neutral-500">Paga bazë:</span> {fmtEUR(s.base_salary)}</div>
                  <div><span className="text-neutral-500">Orë normale:</span> {s.hours_normal}</div>
                  <div><span className="text-neutral-500">Orë shtesë:</span> {s.hours_extra}</div>
                  {s.bonuses > 0 && <div><span className="text-neutral-500">Shtesa:</span> <span className="text-emerald-400">+{fmtEUR(s.bonuses)}</span></div>}
                  {s.deductions > 0 && <div><span className="text-neutral-500">Zbritje:</span> <span className="text-red-400">-{fmtEUR(s.deductions)}</span></div>}
                </div>
                <div className="mt-3 pt-3 border-t border-[#1F2B44] flex items-center justify-between">
                  <div className="text-xs">
                    <div><span className="text-neutral-500">Total:</span> <span className="text-[#FFD600] font-bold">{fmtEUR(s.total)}</span></div>
                    <div><span className="text-neutral-500">Paguar:</span> <span className="text-emerald-400">{fmtEUR(s.paid)}</span></div>
                    {s.remaining > 0 && <div><span className="text-neutral-500">Mbetur:</span> <span className="text-orange-400">{fmtEUR(s.remaining)}</span></div>}
                  </div>
                  {s.paid_at && <div className="text-[10px] text-neutral-500">Pagesa: {s.paid_at}</div>}
                </div>
                {s.note && <div className="text-[10px] text-neutral-500 mt-2 italic">{s.note}</div>}
              </div>
            );
          })}
        </div>
      )}

      {tab === "prof" && profDraft && (
        <div className="space-y-4">
          {/* Contract read-only info */}
          <div className="hg-card p-4">
            <div className="text-xs uppercase text-[#FFD600] font-bold mb-3 flex items-center gap-1"><Briefcase size={14} /> Kontrata (vetëm shikim)</div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><div className="text-neutral-500">Pozicioni</div><div className="font-bold">{profile.position || "—"}</div></div>
              <div><div className="text-neutral-500">Lloji i kontratës</div><div className="font-bold">{CONTRACT_LABELS[profile.contract_type] || "—"}</div></div>
              <div><div className="text-neutral-500">Data e fillimit</div><div className="font-bold">{profile.hire_date || "—"}</div></div>
              <div><div className="text-neutral-500">Skadimi (nëse ka)</div><div className="font-bold">{profile.contract_end_date || "—"}</div></div>
              <div><div className="text-neutral-500">Orë/ditë</div><div className="font-bold">{profile.hours_per_day || 8}h</div></div>
              <div><div className="text-neutral-500">Orë/javë</div><div className="font-bold">{profile.hours_per_week || 40}h</div></div>
              <div><div className="text-neutral-500">Paga mujore bazë</div><div className="font-bold">{fmtEUR(profile.monthly_salary || 0)}</div></div>
              <div><div className="text-neutral-500">Tarifa/orë</div><div className="font-bold">{fmtEUR(profile.hourly_rate || 0)}</div></div>
              <div><div className="text-neutral-500">Tarifa orë shtesë</div><div className="font-bold">{fmtEUR(profile.overtime_rate || 0)}</div></div>
              <div><div className="text-neutral-500">Ditë pushimi/vit</div><div className="font-bold">{profile.vacation_days_total || 21}</div></div>
            </div>
          </div>

          {/* Editable personal data */}
          <div className="hg-card p-4">
            <div className="text-xs uppercase text-[#FFD600] font-bold mb-3 flex items-center gap-1"><UserIcon size={14} /> Të dhënat personale (mund t'i ndryshoni)</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Emri</label>
                <input value={profDraft.first_name || ""} onChange={e => setProfDraft({...profDraft, first_name: e.target.value})}
                  data-testid="prof-first-name" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Mbiemri</label>
                <input value={profDraft.last_name || ""} onChange={e => setProfDraft({...profDraft, last_name: e.target.value})}
                  data-testid="prof-last-name" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400 flex items-center gap-1"><Phone size={10} /> Telefon</label>
                <input value={profDraft.phone || ""} onChange={e => setProfDraft({...profDraft, phone: e.target.value})}
                  data-testid="prof-phone" placeholder="+383 44 ..." className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400 flex items-center gap-1"><Mail size={10} /> E-posta</label>
                <input value={profDraft.email || ""} onChange={e => setProfDraft({...profDraft, email: e.target.value})}
                  data-testid="prof-email" placeholder="email@example.com" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400 flex items-center gap-1"><MapPin size={10} /> Adresa</label>
                <input value={profDraft.address || ""} onChange={e => setProfDraft({...profDraft, address: e.target.value})}
                  data-testid="prof-address" placeholder="Rruga, qyteti" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400">Nr. personal</label>
                <input value={profDraft.personal_id || ""} onChange={e => setProfDraft({...profDraft, personal_id: e.target.value})}
                  data-testid="prof-personal-id" className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-neutral-400 flex items-center gap-1"><Landmark size={10} /> Banka</label>
                <input value={profDraft.bank_name || ""} onChange={e => setProfDraft({...profDraft, bank_name: e.target.value})}
                  data-testid="prof-bank" placeholder="p.sh. NLB, ProCredit..." className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-neutral-400">IBAN / Nr. i llogarisë</label>
                <input value={profDraft.iban || ""} onChange={e => setProfDraft({...profDraft, iban: e.target.value})}
                  data-testid="prof-iban" placeholder="XK05 ..." className="w-full bg-[#131E30] border border-[#1F2B44] px-2 py-2 mt-1 font-mono" />
              </div>
            </div>
            <button onClick={saveProfile} disabled={profSaving} data-testid="prof-save"
              className="mt-4 hg-btn-primary py-2.5 px-4 text-xs uppercase w-full inline-flex items-center justify-center gap-1">
              <Save size={12} /> {profSaving ? "Duke ruajtur..." : "Ruaj të dhënat"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
